import {
  addDays,
  isSaturday,
  isSunday,
  isValid,
  parseISO,
  setHours,
  startOfDay,
  subDays,
} from "date-fns";

import type { ClosingTime } from "@luna-restaurant/school-api";

import {
  compareDateOnly,
  dateOnlyToLocalDate,
  getBerlinTimeOfDayInMilliseconds,
  getBerlinTodayDateOnly,
} from "@/utils/closingTimes/berlinDateOnly";

export const isClosedDay = (
  date: Date,
  closingTimes: ClosingTime[],
): boolean => {
  const day = startOfDay(date);
  return closingTimes.some((ct) => {
    if (ct.type !== "PUBLIC_HOLIDAY") return false;
    const start = startOfDay(parseISO(ct.startDate));
    const end = startOfDay(parseISO(ct.endDate));
    return day >= start && day <= end;
  });
};

const MAX_ITERATIONS = 365;
const DEADLINE_HOUR = 12;

const getLocalDateOnly = (date: Date): string =>
  [
    String(date.getFullYear()),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const isNonBusinessDay = (date: Date, closingTimes: ClosingTime[]): boolean =>
  isSaturday(date) || isSunday(date) || isClosedDay(date, closingTimes);

type BerlinNow = {
  dateOnly: string;
  timeOfDayInMilliseconds: number;
};

/**
 * The Berlin helpers throw when `Intl` cannot resolve Europe/Berlin. These
 * deadline checks run during render and the school app has no error boundary,
 * so a broken `Intl` must not take the screen down: fail closed instead, which
 * disables ordering/cancelling rather than silently allowing it.
 */
const readBerlinNow = (now: Date): BerlinNow | null => {
  try {
    return {
      dateOnly: getBerlinTodayDateOnly(now),
      timeOfDayInMilliseconds: getBerlinTimeOfDayInMilliseconds(now),
    };
  } catch (error) {
    console.error(
      "Unable to determine the current time in Europe/Berlin",
      error,
    );
    return null;
  }
};

export const getOrderDeadline = (
  targetDate: Date,
  closingTimes: ClosingTime[],
): Date => {
  if (!isValid(targetDate)) return new Date(NaN);
  let candidate = startOfDay(subDays(targetDate, 1));

  for (let i = 0; i < MAX_ITERATIONS; i++) {
    if (!isNonBusinessDay(candidate, closingTimes)) {
      return setHours(candidate, DEADLINE_HOUR);
    }
    candidate = subDays(candidate, 1);
  }

  return new Date(NaN);
};

export const isOrderableDate = (
  targetDate: Date,
  now: Date,
  closingTimes: ClosingTime[],
): boolean => {
  if (!isValid(targetDate) || !isValid(now)) return false;
  if (isNonBusinessDay(targetDate, closingTimes)) return false;
  const deadline = getOrderDeadline(targetDate, closingTimes);
  if (!isValid(deadline)) return false;

  const berlinNow = readBerlinNow(now);
  if (!berlinNow) return false;

  const dayComparison = compareDateOnly(
    berlinNow.dateOnly,
    getLocalDateOnly(deadline),
  );
  return (
    dayComparison !== null &&
    (dayComparison < 0 ||
      (dayComparison === 0 &&
        berlinNow.timeOfDayInMilliseconds < DEADLINE_HOUR * 60 * 60 * 1000))
  );
};

export const getEarliestOrderableDate = (
  now: Date,
  closingTimes: ClosingTime[],
): Date => {
  if (!isValid(now)) return new Date(NaN);
  const berlinNow = readBerlinNow(now);
  if (!berlinNow) return new Date(NaN);
  const today = dateOnlyToLocalDate(berlinNow.dateOnly);
  if (!today) return new Date(NaN);
  let candidate = addDays(today, 1);

  for (let i = 0; i < MAX_ITERATIONS; i++) {
    if (isOrderableDate(candidate, now, closingTimes)) {
      return candidate;
    }
    candidate = addDays(candidate, 1);
  }

  return new Date(NaN);
};
